import { Alert, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * The three ways out of a money problem, shared by every screen that has one.
 *
 * There are exactly two such screens and they used to disagree: the activation
 * screen offered a card payment, the office/ОСМП route and a [Обновить] re-poll,
 * while the financial-block screen — the state the MOST likely visitor of this
 * portal is in — offered only a sentence pointing at a Personal Area that does not
 * exist until Phase 7. A suspended subscriber therefore reached the portal, was told
 * he was suspended, and had no route out of it at all.
 *
 * Extracting the trio keeps the two screens honest by construction: whatever the
 * activation screen can do about a shortfall, the block screen can do about a debt.
 *
 *   [Пополнить картой] — a REAL card payment since Phase 3 (deferred row D44): the
 *                        core starts a PayOnline order and answers a redirect URL,
 *                        and the browser follows it to the acquirer's own secure
 *                        form. It is the FIRST route offered because it is the only
 *                        one a subscriber sitting in the walled garden at midnight
 *                        can actually complete.
 *   office / ОСМП hint — the alternatives, for a subscriber with no card; the
 *                        payment lands in minutes.
 *   [Обновить]         — re-reads GET /captive/account, so the screen flips the
 *                        moment the payment is posted, with no re-login. This is
 *                        also what completes a CARD payment: the callback credits
 *                        the balance server-to-server and this poll is what notices.
 *
 * A 503 is still possible and still calm: an installation whose acquirer is not
 * configured answers captive.topup_unavailable, which the callers render as
 * `topUpNotice` — never as an error, because this page is the only page a
 * subscriber with no service can see.
 */
export function TopUpRoutes({
  topUpPending,
  refreshing,
  topUpNotice,
  onTopUp,
  onRefresh,
}: {
  topUpPending: boolean;
  refreshing: boolean;
  /** Calm notice about the card route (503 stub / transport trouble). */
  topUpNotice: string | null;
  onTopUp: () => void;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {topUpNotice && (
        <Alert type="info" showIcon message={topUpNotice} style={{ marginBottom: 16 }} />
      )}
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Button type="primary" block loading={topUpPending} onClick={onTopUp}>
          {t('topup.card')}
        </Button>
        <p className="portal-hint" style={{ margin: 0, textAlign: 'center' }}>
          {t('topup.cardHint')}
        </p>
        <Alert type="info" message={t('topup.officeTitle')} description={t('topup.officeHint')} />
        <Button block loading={refreshing} onClick={onRefresh}>
          {t('topup.refresh')}
        </Button>
      </Space>
    </>
  );
}
